import React, { useState } from 'react';
import { CheckIcon, AlertTriangleIcon, LoaderIcon, ArrowRightIcon } from 'lucide-react';
import { PageIntro } from '../components/PageIntro';
import { CONTACT_EMAIL } from '../data/site';
type Status = 'idle' | 'submitting' | 'success' | 'error';
interface FormValues {
  name: string;
  email: string;
  organization: string;
  problem: string;
}
const emptyForm: FormValues = {
  name: '',
  email: '',
  organization: '',
  problem: ''
};
const fieldClass = 'mt-2 w-full border border-ink-200 bg-paper-raised px-3.5 py-2.5 text-[15px] text-ink-950 placeholder:text-ink-300 transition-colors duration-150 ease-out focus:border-accent focus:outline-none focus-visible:ring-1 focus-visible:ring-accent';
const labelClass = 'block text-[13px] text-ink-700';
export function Contact() {
  const [values, setValues] = useState<FormValues>(emptyForm);
  const [status, setStatus] = useState<Status>('idle');
  const update = (key: keyof FormValues) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValues(previous => ({
      ...previous,
      [key]: event.target.value
    }));
    if (status === 'error' || status === 'success') setStatus('idle');
  };
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('submitting');
    try {
      await new Promise<void>((resolve, reject) => {
        window.setTimeout(() => {
          if (values.email.includes('@')) resolve();else reject(new Error('Invalid address'));
        }, 900);
      });
      setStatus('success');
      setValues(emptyForm);
    } catch {
      setStatus('error');
    }
  };
  return <>
      <PageIntro eyebrow="Contact" title="Tell us about the problem that is not going away." lead="If you have hard problems that need solving that you think may be addressable via Research Science Group, please contact us and let's explore how we may work together." data-id="element-98" data-mp="vjlvcoby6zu5" />

      <section className="mx-auto max-w-6xl px-6 py-20 lg:px-10" data-id="element-99" data-mp="2bxc1s1qji06y">
        <div className="grid gap-14 lg:grid-cols-[1.4fr_1fr] lg:gap-24" data-id="element-100" data-mp="1m5sgdm0j3sgz">
          <div data-id="element-101" data-mp="1h92130wjt0wm">
            {status === 'success' ? <div role="status" className="border border-accent bg-accent-soft px-6 py-10 text-center" data-id="element-102" data-mp="1hamv8peyahty">
                <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-accent text-paper" data-id="element-103" data-mp="1r7hrt2rny85l">
                  <CheckIcon aria-hidden="true" className="h-5 w-5" data-id="element-104" data-mp="13h5e1b9qlh73" />
                </span>
                <h2 className="mt-5 font-display text-[24px] text-ink-950" data-id="element-105" data-mp="yjcakpztj1ai">Message received</h2>
                <p className="mx-auto mt-3 max-w-sm text-[15px] leading-relaxed text-ink-700" data-id="element-106" data-mp="1d88dy9qxjq4s">
                  A principal will read this personally and reply within two business days — usually
                  with questions before anything else.
                </p>
                <button type="button" onClick={() => setStatus('idle')} className="mt-6 text-sm text-accent underline decoration-1 underline-offset-4 transition-colors duration-150 ease-out hover:text-accent-hover" data-id="element-107" data-mp="fs2z4b6hm2ga">
                  Send another message
                </button>
              </div> : <form onSubmit={handleSubmit} noValidate data-id="element-108" data-mp="2mf0k9q600gi3">
                <h2 className="font-display text-[26px] text-ink-950" data-id="element-109" data-mp="2mdh3zzkce5gb">Send an inquiry</h2>
                <p className="mt-2 text-[15px] leading-relaxed text-ink-500" data-id="element-110" data-mp="3a5xm2a42xxfm">
                  The more concrete the description, the more useful our first reply will be.
                </p>

                <div className="mt-8 grid gap-6 sm:grid-cols-2" data-id="element-111" data-mp="313wp7ecrddeh">
                  <div data-id="element-112" data-mp="3559y5xb5dwpm">
                    <label className={labelClass} htmlFor="name" data-id="element-113" data-mp="354qhejx8hed6">
                      Name
                    </label>
                    <input id="name" name="name" required autoComplete="name" value={values.name} onChange={update('name')} className={fieldClass} placeholder="Your name" data-id="element-114" data-mp="1prdsw6ns7obi" />
                  </div>
                  <div data-id="element-115" data-mp="208hd50urtjut">
                    <label className={labelClass} htmlFor="email" data-id="element-116" data-mp="205v8pa36fkgt">
                      Email
                    </label>
                    <input id="email" name="email" type="email" required autoComplete="email" value={values.email} onChange={update('email')} className={fieldClass} placeholder="you@company.com" aria-describedby={status === 'error' ? 'form-error' : undefined} data-id="element-117" data-mp="2sxeun1r5rcmi" />
                  </div>
                </div>

                <div className="mt-6" data-id="element-118" data-mp="13gnhpmjsgj5i">
                  <label className={labelClass} htmlFor="organization" data-id="element-119" data-mp="3f5k8ki2ii67f">
                    Organization <span className="text-ink-300" data-id="element-120" data-mp="3f3hbxf6utetq">(optional)</span>
                  </label>
                  <input id="organization" name="organization" autoComplete="organization" value={values.organization} onChange={update('organization')} className={fieldClass} placeholder="Company, lab, or agency" data-id="element-121" data-mp="3j4jadtsy5mun" />
                </div>

                <div className="mt-6" data-id="element-122" data-mp="6rbndv2bkhpa">
                  <label className={labelClass} htmlFor="problem" data-id="element-123" data-mp="aqu7iombgor2">
                    The problem
                  </label>
                  <textarea id="problem" name="problem" required rows={6} value={values.problem} onChange={update('problem')} className={`${fieldClass} resize-y`} placeholder="What are you trying to do, what have you tried, and where does it stall?" data-id="element-124" data-mp="fovsxg77t05p" />
                </div>

                {status === 'error' && <p id="form-error" role="alert" className="mt-5 flex items-start gap-2 border border-ink-200 bg-paper-sunk px-3.5 py-3 text-[14px] text-ink-900" data-id="element-125" data-mp="3ttx9n96c2lad">
                    <AlertTriangleIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-ink-700" data-id="element-126" data-mp="7jkzb7a4jemm" />
                    We could not send that. Check the email address and try again, or write to{' '}
                    {CONTACT_EMAIL}.
                  </p>}

                <button type="submit" disabled={status === 'submitting'} className="group mt-8 inline-flex items-center gap-2 bg-ink-950 px-5 py-3 text-sm text-paper transition-colors duration-150 ease-out hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-ink-500" data-id="element-127" data-mp="ceqr2mq7jnim">
                  {status === 'submitting' ? <>
                      <LoaderIcon aria-hidden="true" className="h-4 w-4 animate-spin" data-id="element-128" data-mp="v3an7frg3pwg" />
                      Sending
                    </> : <>
                      Send inquiry
                      <ArrowRightIcon aria-hidden="true" className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" data-id="element-129" data-mp="qzuw0yy5ahr5" />
                    </>}
                </button>
              </form>}
          </div>

          <aside className="lg:pt-2" data-id="element-130" data-mp="3b1u8luudfom">
            <div className="border-t border-ink-950 pt-5" data-id="element-131" data-mp="3c4n2zbq5s76">
              <p className="text-[11px] uppercase tracking-label text-ink-500" data-id="element-132" data-mp="7a2du6l58mco">Email</p>
              <a href={`mailto:${CONTACT_EMAIL}`} className="mt-2 block font-display text-[20px] text-ink-950 underline decoration-ink-300 decoration-1 underline-offset-4 transition-colors duration-150 ease-out hover:text-accent hover:decoration-accent" data-id="element-133" data-mp="nptmv8sd0h8k">
                {CONTACT_EMAIL}
              </a>
            </div>

            <div className="mt-10 border-t border-ink-200 pt-5" data-id="element-134" data-mp="4rquck0ls260">
              <p className="text-[11px] uppercase tracking-label text-ink-500" data-id="element-135" data-mp="4u3k3jclrgtm">
                What happens next
              </p>
              <ol className="mt-4 space-y-4" data-id="element-136" data-mp="3r7bqf413n236">
                {['We read the description and reply with clarifying questions.', 'A short technical conversation with the people who would do the work.', 'A written assessment of feasibility, approach, and cost before any commitment.'].map((step, index) => <li key={step} className="flex gap-3 text-[15px] leading-relaxed text-ink-700" data-id="element-137" data-mp="1sprvcnyfl43b" data-mpi={index}>
                    <span className="font-display text-sm text-accent" data-id="element-138" data-mp="1wrd2rts74q64">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    {step}
                  </li>)}
              </ol>
            </div>

            <div className="mt-10 border-t border-ink-200 pt-5" data-id="element-139" data-mp="26p0z2o5ga5qe">
              <p className="text-[11px] uppercase tracking-label text-ink-500" data-id="element-140" data-mp="192k3bncd806r">Confidentiality</p>
              <p className="mt-3 text-[15px] leading-relaxed text-ink-700" data-id="element-141" data-mp="1e0c313j94qli">
                We routinely work under NDA and in restricted environments. Say as much or as little
                as you are able to in the first message.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </>;
}